import { useState, useEffect, useRef } from 'react';
import { Elements } from '../index';

interface ShareDropdownProps {
    url?: string;
    title?: string;
    className?: string;
    buttonLabel?: string;
    size?: 'sm' | 'md' | 'lg';
}

export const ShareDropdown = ({
    url,
    title,
    className = '',
    buttonLabel = 'Share',
    size = 'md',
}: ShareDropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const a2aIconsRef = useRef<HTMLDivElement>(null);
    const scriptLoadedRef = useRef(false);

    // Load AddToAny script
    useEffect(() => {
        if (scriptLoadedRef.current) return;

        const existingScript = document.querySelector('script[src="https://static.addtoany.com/menu/page.js"]');
        if (existingScript) {
            scriptLoadedRef.current = true;
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://static.addtoany.com/menu/page.js';
        script.defer = true;
        script.async = true;
        document.body.appendChild(script);
        scriptLoadedRef.current = true;
    }, []);

    // Initialize AddToAny icons container
    useEffect(() => {
        if (!isOpen || !a2aIconsRef.current) return;

        const initIcons = () => {
            if (typeof (window as any).a2a !== 'undefined' && (window as any).a2a.init_all) {
                (window as any).a2a.init_all();
            } else {
                setTimeout(initIcons, 100);
            }
        };

        setTimeout(initIcons, 50);
    }, [isOpen]);

    const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
    const shareTitle = title || (typeof document !== 'undefined' ? document.title : '');

    const handleShare = (service: string) => {
        if (service === 'copy_link') {
            navigator.clipboard.writeText(shareUrl).then(() => {
                setIsOpen(false);
            }).catch(() => {
                setIsOpen(false);
            });
            return;
        }
        
        if (service === 'telegram') {
            const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`;
            window.open(telegramUrl, '_blank', 'noopener,noreferrer');
            setIsOpen(false);
            return;
        }

        // Use AddToAny to share for other services
        const triggerAddToAnyShare = () => {
            if (typeof (window as any).a2a !== 'undefined') {
                // Create temporary AddToAny button
                const tempContainer = document.createElement('div');
                tempContainer.className = 'a2a_kit a2a_kit_size_32 a2a_default_style';
                tempContainer.style.position = 'fixed';
                tempContainer.style.left = '-9999px';
                tempContainer.style.top = '-9999px';

                const tempButton = document.createElement('a');
                tempButton.className = `a2a_button_${service}`;
                tempButton.href = '#';

                tempContainer.appendChild(tempButton);
                document.body.appendChild(tempContainer);

                // Initialize AddToAny
                if ((window as any).a2a && (window as any).a2a.init_all) {
                    (window as any).a2a.init_all();
                }

                // Set URL and title
                if (shareUrl) {
                    tempButton.setAttribute('data-url', shareUrl);
                }
                if (shareTitle) {
                    tempButton.setAttribute('data-title', shareTitle);
                }

                // Trigger click
                setTimeout(() => {
                    tempButton.click();
                    setTimeout(() => {
                        if (document.body.contains(tempContainer)) {
                            document.body.removeChild(tempContainer);
                        }
                    }, 100);
                    setIsOpen(false);
                }, 200);
            } else {
                setTimeout(triggerAddToAnyShare, 100);
            }
        };

        triggerAddToAnyShare();
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        const timeoutId = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside, true);
        }, 10);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener('mousedown', handleClickOutside, true);
        };
    }, [isOpen]);

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            <Elements.Button
                variant='primary'
                outline={true}
                size={size}
                aria-expanded={isOpen}
                aria-haspopup="true"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsOpen((prev) => !prev);
                }}
                className="shrink-0 md:w-full max-w-[75px]"
            >
                {buttonLabel}
            </Elements.Button>

            {isOpen && (
                <div
                    className="absolute left-0 top-full mt-2 w-56 rounded-lg border border-gray-200 bg-white shadow-lg z-[9999] overflow-hidden p-2"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                >
                    <style>{`
                        .share-dropdown-custom .a2a_kit {
                            display: flex !important;
                            flex-direction: column !important;
                            padding: 0.75rem !important;
                            width: 100% !important;
                            row-gap: 0.75rem !important;
                            column-gap: 0 !important;
                        }
                        .share-dropdown-custom .a2a_kit a {
                            display: flex !important;
                            align-items: center !important;
                            padding: 1rem !important;
                            text-decoration: none !important;
                            color: #374151 !important;
                            font-size: 0.875rem !important;
                            background: transparent !important;
                            border-radius: 0.375rem !important;
                            width: 100% !important;
                            min-height: 40px !important;
                            height: auto !important;
                            cursor: pointer !important;
                            gap: 0.75rem !important;
                            margin: 0 !important;
                        }
                        .share-dropdown-custom .a2a_kit a:hover {
                            background-color: #f3f4f6 !important;
                        }
                        .share-dropdown-custom .a2a_kit .a2a_dd {
                            display: none !important;
                        }
                        .share-dropdown-custom .a2a_kit a span {
                            display: none !important;
                        }
                        .share-dropdown-custom .a2a_kit a svg,
                        .share-dropdown-custom .a2a_kit a img {
                            width: 20px !important;
                            height: 20px !important;
                            flex-shrink: 0 !important;
                            margin: 0 !important;
                        }
                        .share-dropdown-custom .a2a_kit a::after {
                            content: attr(data-label) !important;
                            display: block !important;
                            margin-left: 0 !important;
                        }
                    `}</style>
                    <div
                        ref={a2aIconsRef}
                        className="share-dropdown-custom a2a_kit a2a_kit_size_32 a2a_default_style"
                        data-a2a-url={shareUrl}
                        data-a2a-title={shareTitle}
                    >
                        <a className="a2a_dd" href="https://www.addtoany.com/share" style={{ padding: '5px' }}></a>
                        <a
                            className="a2a_button_copy_link"
                            data-label="Copy Link"
                            href="#"
                            style={{ padding: '5px' }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleShare('copy_link');
                            }}
                        ></a>

                        <a
                            className="a2a_button_email"
                            data-label="Email"
                            href="#"
                            style={{ padding: '5px' }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleShare('email');
                            }}
                        ></a>
                        <a
                            className="a2a_button_linkedin"
                            data-label="LinkedIn"
                            href="#"
                            style={{ padding: '5px' }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleShare('linkedin');
                            }}
                        ></a>
                        <a
                            className="a2a_button_telegram"
                            data-label="Telegram"
                            href="#"
                            style={{ padding: '5px' }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleShare('telegram');
                            }}
                        ></a>
                        <a
                            className="a2a_button_facebook_messenger"
                            data-label="Facebook Messenger"
                            href="#"
                            style={{ padding: '5px' }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleShare('facebook_messenger');
                            }}
                        ></a>
                        <a
                            className="a2a_button_threads"
                            data-label="Threads"
                            href="#"
                            style={{ padding: '5px' }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleShare('threads');
                            }}
                        ></a>
                        <a
                            className="a2a_button_microsoft_teams"
                            data-label="Microsoft Teams"
                            href="#"
                            style={{ padding: '5px' }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleShare('microsoft_teams');
                            }}
                        ></a>
                        <a
                            className="a2a_button_whatsapp"
                            data-label="WhatsApp"
                            href="#"
                            style={{ padding: '5px' }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleShare('whatsapp');
                            }}
                        ></a>
                        <a
                            className="a2a_button_facebook"
                            data-label="Facebook"
                            href="#"
                            style={{ padding: '5px' }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleShare('facebook');
                            }}
                        ></a>
                        <a
                            className="a2a_button_x"
                            data-label="X (Twitter)"
                            href="#"
                            style={{ padding: '5px' }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleShare('x');
                            }}
                        ></a>

                    </div>
                </div>
            )}
        </div>
    );
};
